import { useEffect, useRef } from 'react';

function easeInOutCubic(x: number): number {
	return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

interface ParkingLoaderProps {
	size?: number;
	label?: string;
}

export function ParkingLoader({ size = 120, label = 'Loading…' }: ParkingLoaderProps) {
	const pPath = 'M44 86V34h22c12 0 20 8 20 18s-8 18-20 18H44';
	const strokeRef = useRef<SVGPathElement>(null);
	const dotRef = useRef<SVGCircleElement>(null);

	useEffect(() => {
		const pathEl = strokeRef.current;
		const dotEl = dotRef.current;
		if (!pathEl || !dotEl) return;

		const prefersReduced =
			typeof window !== 'undefined' &&
			window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

		if (prefersReduced) {
			pathEl.setAttribute('stroke-dashoffset', '0');
			dotEl.style.display = 'none';
			return;
		}

		const durationMs = 3200;
		const totalLen = pathEl.getTotalLength();

		let raf = 0;
		let start = 0;

		const tick = (now: number) => {
			if (!start) start = now;
			const t = ((now - start) % durationMs) / durationMs;

			// triangle wave: 0..1..0
			const tri = t < 0.5 ? t * 2 : 1 - (t - 0.5) * 2;
			const eased = easeInOutCubic(tri);

			// draw/undraw (pathLength=100 makes this stable)
			const dashOffset = (1 - eased) * 100;
			pathEl.setAttribute('stroke-dashoffset', dashOffset.toFixed(2));

			// move dot along the real path length
			const l = eased * totalLen;
			const p = pathEl.getPointAtLength(l);
			dotEl.setAttribute('cx', p.x.toFixed(2));
			dotEl.setAttribute('cy', p.y.toFixed(2));

			raf = requestAnimationFrame(tick);
		};

		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, []);

	return (
		<div
			role="status"
			aria-live="polite"
			aria-label={label}
			style={{ width: size, height: size, display: 'inline-flex' }}
		>
			<svg
				width={size}
				height={size}
				viewBox="0 0 120 120"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				{/* Frame */}
				<rect
					x="10"
					y="10"
					width="100"
					height="100"
					rx="22"
					stroke="currentColor"
					strokeWidth="1.5"
				/>

				{/* Draw / undraw stroke (driven by JS for perfect sync with the dot) */}
				<path
					ref={strokeRef}
					d={pPath}
					pathLength={100}
					stroke="currentColor"
					strokeWidth="1.5"
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeDasharray="100 100"
					strokeDashoffset="100"
				/>

				{/* Drawing dot */}
				<circle
					ref={dotRef}
					cx="44"
					cy="86"
					r="3.2"
					fill="currentColor"
				/>

				{/* Parking slots */}
				<g className="pm-slots">
					<rect
						x="26"
						y="92"
						width="16"
						height="8"
						rx="3"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
					<rect
						x="52"
						y="92"
						width="16"
						height="8"
						rx="3"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
					<rect
						x="78"
						y="92"
						width="16"
						height="8"
						rx="3"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
					/>
				</g>

				{/* Subtle ground line */}
				<path
					d="M26 86h68"
					stroke="currentColor"
					strokeWidth="1.5"
					strokeLinecap="round"
				/>

				<style>{`
					.pm-slots rect {
						transform-box: fill-box;
						transform-origin: center;
					}
					.pm-slots rect:nth-child(1) { animation: pm-slot 1.8s ease-in-out infinite; }
					.pm-slots rect:nth-child(2) { animation: pm-slot 1.8s ease-in-out infinite 0.3s; }
					.pm-slots rect:nth-child(3) { animation: pm-slot 1.8s ease-in-out infinite 0.6s; }
					@keyframes pm-slot {
						0%, 100% { transform: translateY(2px); }
						50%      { transform: translateY(0px); }
					}
				`}</style>
			</svg>
		</div>
	);
}
